import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const CATEGORIES = ['Wszystkie', 'AI & Automatyzacja', 'SEO & Marketing', 'Strony & UX', 'EUDR & Prawo'];

const POSTS = [
  {
    id: 1,
    title: 'Jak agenci AI rewolucjonizują obsługę klienta w branży turystycznej',
    excerpt: 'W 2025 roku hotele i ośrodki, które wdrożyły agentów AI do obsługi zapytań, notują o 40–70% mniej czasu poświęcanego na rutynowe pytania. Jak to działa i co konkretnie możesz zautomatyzować już dziś?',
    category: 'AI & Automatyzacja',
    readTime: '7 min',
    date: '20 marca 2026',
    color: '#00d4ff',
    featured: true,
    img: 'https://public.readdy.ai/ai/img_res/c39b42caad813b7cde1b70854a81c1cd.jpg',
    tags: ['Agent AI', 'Automatyzacja', 'Turystyka'],
    author: 'Orsonet',
    authorRole: 'Ekspert AI',
  },
  {
    id: 2,
    title: 'EUDR a AI — jak nowe regulacje UE stają się przewagą konkurencyjną',
    excerpt: 'Rozporządzenie EUDR wchodzi w życie i zmusza tysiące firm do udokumentowania łańcucha dostaw. Firmy automatyzujące ten proces z pomocą AI nie tylko spełniają wymogi — zdobywają realną przewagę rynkową.',
    category: 'EUDR & Prawo',
    readTime: '9 min',
    date: '15 marca 2026',
    color: '#00ff88',
    featured: false,
    img: 'https://readdy.ai/api/search-image?query=EU%20regulation%20compliance%20digital%20data%20tracking%20forest%20supply%20chain%20sustainability%20technology%20professional%20dark%20minimal%20interface%20legal%20tech%20green&width=800&height=460&seq=blog2eudr2026&orientation=landscape',
    tags: ['EUDR', 'Compliance', 'AI'],
    author: 'Orsonet',
    authorRole: 'Specjalista EUDR',
  },
  {
    id: 3,
    title: 'Local SEO w 2026: Kompletny przewodnik dla lokalnych biznesów',
    excerpt: '46% wszystkich wyszukiwań ma charakter lokalny. Jeśli nie masz strategii Local SEO, tracisz klientów, którzy są dosłownie kilka kilometrów od Ciebie — i szukają Cię teraz.',
    category: 'SEO & Marketing',
    readTime: '11 min',
    date: '8 marca 2026',
    color: '#ff6b9d',
    featured: false,
    img: 'https://readdy.ai/api/search-image?query=local%20SEO%20Google%20Maps%20optimization%20dark%20tech%20interface%20location%20pins%20analytics%20professional%20minimalist%20visualization%20search%20ranking&width=800&height=460&seq=blog3seo2026&orientation=landscape',
    tags: ['Local SEO', 'Google Maps', 'Pozycjonowanie'],
    author: 'Orsonet',
    authorRole: 'SEO Strategist',
  },
  {
    id: 4,
    title: '7 elementów strony hotelu, które podwajają liczbę bezpośrednich rezerwacji',
    excerpt: 'Booking.com pobiera 15–25% prowizji. Każda rezerwacja przez własną stronę to czyste pieniądze zostające w kieszeni właściciela. Które elementy decydują o konwersji?',
    category: 'Strony & UX',
    readTime: '8 min',
    date: '1 marca 2026',
    color: '#00d4ff',
    featured: false,
    img: 'https://readdy.ai/api/search-image?query=premium%20hotel%20website%20dark%20elegant%20booking%20system%20UX%20design%20conversion%20optimization%20luxury%20accommodation%20modern%20web%20interface%20professional%20cinematic&width=800&height=460&seq=blog4hotel2026&orientation=landscape',
    tags: ['Strony WWW', 'UX', 'Hotele'],
    author: 'Orsonet',
    authorRole: 'UX Designer',
  },
  {
    id: 5,
    title: 'Wdrożenie AI w małej firmie — od czego zacząć i jakich błędów unikać',
    excerpt: 'Większość małych firm popełnia ten sam błąd: kupuje drogie narzędzia AI bez żadnej strategii. Tymczasem najskuteczniejsze wdrożenia zaczynają się od zidentyfikowania jednego konkretnego problemu.',
    category: 'AI & Automatyzacja',
    readTime: '6 min',
    date: '22 lutego 2026',
    color: '#00ff88',
    featured: false,
    img: 'https://readdy.ai/api/search-image?query=small%20business%20AI%20strategy%20planning%20digital%20transformation%20professional%20dark%20office%20technology%20growth%20startup%20modern%20workspace%20minimal&width=800&height=460&seq=blog5impl2026&orientation=landscape',
    tags: ['AI', 'Strategia', 'MŚP'],
    author: 'Orsonet',
    authorRole: 'AI Consultant',
  },
  {
    id: 6,
    title: 'Google Wizytówka 2026 — dominacja wyników lokalnych i 5-gwiazdkowa reputacja',
    excerpt: '\"Gdzie zjeść w Polańczyku?\" — jeśli Twoja restauracja nie pojawia się w top 3 Google Maps, niemal nie istniejesz dla nowego klienta. Kompletna metodologia Google Business Profile.',
    category: 'SEO & Marketing',
    readTime: '10 min',
    date: '14 lutego 2026',
    color: '#ff6b9d',
    featured: false,
    img: 'https://readdy.ai/api/search-image?query=Google%20Business%20Profile%20local%20search%20optimization%20reviews%20stars%20rating%20dark%20interface%20digital%20marketing%20visualization%20maps%20professional%20analytics&width=800&height=460&seq=blog6gbp2026&orientation=landscape',
    tags: ['Google Wizytówka', 'Opinie', 'Local SEO'],
    author: 'Orsonet',
    authorRole: 'Local SEO Expert',
  },
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Wszystkie');
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const featured = POSTS.find((p) => p.featured);
  const regular = POSTS.filter((p) => !p.featured);
  const filtered = activeCategory === 'Wszystkie'
    ? regular
    : regular.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.07) 0%, transparent 55%)' }}
        />
        <div className="max-w-[1100px] mx-auto text-center relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
              <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Wiedza & Ekspertyza</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-6">
              Blog AI & Digital<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}>
                Orsonet
              </span>
            </h1>
            <p className="text-xl text-white/45 max-w-2xl mx-auto leading-relaxed">
              Praktyczna wiedza o AI, SEO i technologiach dla polskich firm. Bez bullshitu — tylko to, co naprawdę działa.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="pb-14 px-4 md:px-6">
          <div className="max-w-[1400px] mx-auto">
            <AnimatedSection>
              <div className="group relative rounded-3xl overflow-hidden border border-white/8 hover:border-white/18 transition-all duration-500 cursor-pointer"
                style={{ height: 'clamp(320px, 45vw, 560px)' }}>
                <img src={featured.img} alt={featured.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-800" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Top badges */}
                <div className="absolute top-6 left-6 flex items-center gap-3 z-10">
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-sm"
                    style={{ background: `${featured.color}25`, border: `1px solid ${featured.color}45`, color: featured.color }}>
                    Polecany artykuł
                  </span>
                  <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-black/50 border border-white/20 text-white/55 backdrop-blur-sm">
                    {featured.category}
                  </span>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 z-10 p-8 md:p-12 max-w-[640px]">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-white/40 text-sm">{featured.date}</span>
                    <span className="w-1 h-1 rounded-full bg-white/30" />
                    <span className="text-white/40 text-sm">{featured.readTime} czytania</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl font-black text-white mb-4 leading-tight">{featured.title}</h2>
                  <p className="text-white/55 text-sm md:text-base leading-relaxed mb-6 hidden md:block">{featured.excerpt}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {featured.tags.map((t) => (
                      <span key={t} className="px-2.5 py-1 rounded-lg text-xs font-medium text-white/40 border border-white/10">#{t}</span>
                    ))}
                  </div>
                  <Link to="/kontakt"
                    className="inline-flex items-center gap-2 text-black px-6 py-3 rounded-xl font-bold text-sm whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                    style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)' }}>
                    Czytaj artykuł
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Filter */}
      <section className="px-4 md:px-6 pb-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-black text-white">Wszystkie artykuły</h2>
              <p className="text-white/35 text-sm mt-0.5">{filtered.length} artykułów</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className="px-4 py-2 rounded-full text-xs font-semibold transition-all duration-250 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeCategory === cat ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeCategory === cat ? '1px solid rgba(0,212,255,0.4)' : '1px solid rgba(255,255,255,0.09)',
                    color: activeCategory === cat ? '#00d4ff' : 'rgba(255,255,255,0.45)',
                  }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
            {filtered.map((post, idx) => (
              <AnimatedSection key={post.id} delay={idx * 0.07}>
                <div
                  className="group cursor-pointer h-full flex flex-col rounded-2xl overflow-hidden border border-white/8 bg-white/[0.02] hover:border-white/18 transition-all duration-400 hover:-translate-y-1"
                  onMouseEnter={() => setHoveredId(post.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden flex-shrink-0" style={{ height: '220px' }}>
                    <img src={post.img} alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-106 transition-transform duration-700"
                      style={{ transform: hoveredId === post.id ? 'scale(1.06)' : 'scale(1)', transition: 'transform 0.7s cubic-bezier(0.4,0,0.2,1)' }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/80 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-2.5 py-1.5 rounded-full text-[10px] font-bold backdrop-blur-sm"
                        style={{ background: `${post.color}20`, border: `1px solid ${post.color}40`, color: post.color }}>
                        {post.category}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-sm text-white/50 text-[10px]">
                        <i className="ri-time-line" />{post.readTime}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-white/30 text-xs">{post.date}</span>
                      <span className="w-1 h-1 rounded-full bg-white/20" />
                      <span className="text-xs font-semibold" style={{ color: post.color }}>{post.authorRole}</span>
                    </div>
                    <h3 className="text-lg font-black text-white mb-3 leading-snug group-hover:text-[#00d4ff] transition-colors">{post.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed mb-5 flex-1 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1.5">
                        {post.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded text-[10px] font-medium text-white/30 border border-white/8">#{tag}</span>
                        ))}
                      </div>
                      <Link to="/kontakt"
                        className="flex items-center gap-1.5 text-xs font-bold flex-shrink-0 ml-3 group-hover:gap-2.5 transition-all whitespace-nowrap"
                        style={{ color: post.color }}>
                        Czytaj
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Topics strip */}
      <section className="py-16 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-10">
              <p className="text-white/35 text-xs uppercase tracking-[2px] font-semibold mb-3">Tematy, o których piszemy</p>
              <h2 className="text-3xl font-black text-white">Obszary ekspertyzy Orsonet</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {[
              { name: 'Agenci AI', icon: 'ri-robot-2-line', count: '12 art.', color: '#00d4ff' },
              { name: 'Local SEO', icon: 'ri-map-pin-2-line', count: '9 art.', color: '#00ff88' },
              { name: 'UX & Konwersja', icon: 'ri-layout-4-line', count: '7 art.', color: '#ff6b9d' },
              { name: 'EUDR', icon: 'ri-file-shield-2-line', count: '5 art.', color: '#00d4ff' },
              { name: 'Google Ads', icon: 'ri-google-line', count: '6 art.', color: '#00ff88' },
              { name: 'Automatyzacja', icon: 'ri-settings-3-line', count: '8 art.', color: '#ff6b9d' },
            ].map((t, idx) => (
              <AnimatedSection key={t.name} delay={idx * 0.06}>
                <div className="p-5 rounded-xl border border-white/8 bg-white/[0.02] hover:border-white/18 transition-all text-center group cursor-pointer hover:-translate-y-0.5">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3"
                    style={{ background: `${t.color}12`, border: `1px solid ${t.color}25` }}>
                    <i className={`${t.icon} text-lg`} style={{ color: t.color }} />
                  </div>
                  <div className="font-bold text-white text-xs group-hover:text-[#00d4ff] transition-colors">{t.name}</div>
                  <div className="text-white/30 text-[10px] mt-1">{t.count}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-[720px] mx-auto">
          <AnimatedSection>
            <div className="p-10 md:p-14 rounded-3xl relative overflow-hidden text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(0,212,255,0.07) 0%, rgba(0,255,136,0.04) 100%)',
                border: '1px solid rgba(0,212,255,0.16)',
              }}>
              <div className="absolute inset-0 pointer-events-none opacity-30"
                style={{
                  backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(0,212,255,0.15) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(0,255,136,0.12) 0%, transparent 50%)',
                }}
              />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
                  <i className="ri-mail-send-line text-3xl text-[#00d4ff]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3">Newsletter AI dla biznesu</h3>
                <p className="text-white/45 text-sm leading-relaxed mb-7 max-w-md mx-auto">
                  Co dwa tygodnie: najważniejsze nowości AI, praktyczne case studies z naszych wdrożeń i gotowe checklisty do zastosowania w firmie.
                </p>
                <div className="flex items-center gap-4 justify-center flex-wrap">
                  {['Co 2 tygodnie', '0 spamu', 'Wypis w 1 klik'].map((b) => (
                    <div key={b} className="flex items-center gap-1.5 text-white/40 text-xs">
                      <i className="ri-checkbox-circle-fill text-[#00ff88] text-sm" />
                      {b}
                    </div>
                  ))}
                </div>
                <div className="mt-7">
                  <Link to="/kontakt"
                    className="inline-flex items-center gap-2 text-black px-8 py-4 rounded-xl font-bold text-sm whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                    style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)', boxShadow: '0 0 20px rgba(0,212,255,0.25)' }}>
                    <i className="ri-send-plane-line" />
                    Zapisz się do newslettera
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
